import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { formatCurrency } from '../utils/format';
import { FileDown, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

export default function Reports() {
  const [subeler, setSubeler] = useState([]);
  const [filters, setFilters] = useState({
    baslangic: '',
    bitis: '',
    subeId: ''
  });
  const [aylikRapor, setAylikRapor] = useState(null);
  const [bankaRapor, setBankaRapor] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSubeler();
    // Set default dates (this month)
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setFilters({
      baslangic: firstDay.toISOString().split('T')[0],
      bitis: today.toISOString().split('T')[0],
      subeId: ''
    });
  }, []);

  const loadSubeler = async () => {
    try {
      const res = await api.getSubeler();
      setSubeler(res.data);
    } catch (error) {
      console.error('Error loading subeler:', error);
    }
  };

  const handleSearch = async () => {
    if (!filters.baslangic || !filters.bitis) {
      toast.error('Lütfen başlangıç ve bitiş tarihlerini seçin');
      return;
    }

    setLoading(true);
    try {
      const params = {
        baslangic: filters.baslangic,
        bitis: filters.bitis
      };
      if (filters.subeId) {
        params.subeId = filters.subeId;
      }

      const [aylikRes, bankaRes] = await Promise.all([
        api.getAylikRapor(params),
        api.getBankaRapor(params)
      ]);

      setAylikRapor(aylikRes.data);
      setBankaRapor(bankaRes.data);
      toast.success('Raporlar yüklendi');
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Raporlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!aylikRapor) {
      toast.error('İndirilecek rapor yok');
      return;
    }

    // Türk Excel için noktalı virgül ayracı kullan
    const sep = ';';
    
    // UTF-8 BOM ekle (Excel'in Türkçe karakterleri doğru göstermesi için)
    const BOM = '\uFEFF';
    
    // Create CSV content with semicolon separator for Turkish Excel
    let csv = BOM;
    csv += `Metrik${sep}Değer\n`;
    csv += `Toplam Satış${sep}${aylikRapor.toplamSatis}\n`;
    csv += `Nakit Satış${sep}${aylikRapor.nakitSatis}\n`;
    csv += `Kart Toplam${sep}${aylikRapor.kartToplam}\n`;
    csv += `Havale Toplam${sep}${aylikRapor.havaleToplam}\n`;
    csv += `Veresiye Satış${sep}${aylikRapor.veresiyeSatis}\n`;
    csv += `Gider Toplam${sep}${aylikRapor.nakitGiderToplam}\n`;
    csv += `NET${sep}${aylikRapor.net}\n\n`;

    csv += `Banka${sep}Kart POS${sep}Havale${sep}Toplam\n`;
    bankaRapor.forEach(banka => {
      csv += `${banka.ad}${sep}${banka.kartPos}${sep}${banka.havale}${sep}${banka.toplam}\n`;
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `rapor_${filters.baslangic}_${filters.bitis}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Rapor indirildi');
  };

  const paymentMethodData = aylikRapor ? [
    { name: 'Nakit', value: aylikRapor.nakitSatis },
    { name: 'Kart', value: aylikRapor.kartToplam },
    { name: 'Havale', value: aylikRapor.havaleToplam },
    { name: 'Veresiye', value: aylikRapor.veresiyeSatis }
  ] : [];

  return (
    <div className="space-y-4 md:space-y-8" data-testid="reports-page">
      <div>
        <h1 className="text-xl md:text-3xl font-heading font-bold text-textPrimary">Raporlar</h1>
        <p className="text-xs md:text-base text-textSecondary mt-1 md:mt-2">Detaylı finansal raporlar ve analizler</p>
      </div>

      {/* Filters */}
      <div className="bg-surface border border-border p-4 md:p-6">
        <h2 className="text-base md:text-lg font-heading font-bold text-textPrimary mb-3 md:mb-4">Filtreler</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div>
            <label className="block text-sm font-medium text-textPrimary mb-2">Başlangıç Tarihi</label>
            <Input
              type="date"
              data-testid="baslangic-tarih-input"
              value={filters.baslangic}
              onChange={(e) => setFilters({ ...filters, baslangic: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-textPrimary mb-2">Bitiş Tarihi</label>
            <Input
              type="date"
              data-testid="bitis-tarih-input"
              value={filters.bitis}
              onChange={(e) => setFilters({ ...filters, bitis: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-textPrimary mb-2">Şube (Opsiyonel)</label>
            <Select
              value={filters.subeId || "all"}
              onValueChange={(value) => setFilters({ ...filters, subeId: value === "all" ? "" : value })}
            >
              <SelectTrigger data-testid="sube-select">
                <SelectValue placeholder="Tüm şubeler" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Şubeler</SelectItem>
                {subeler.map((sube) => (
                  <SelectItem key={sube.id} value={sube.id}>{sube.ad}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              onClick={handleSearch}
              data-testid="search-btn"
              disabled={loading}
              className="w-full bg-primary hover:bg-primaryHover text-primaryForeground"
            >
              <Search className="h-4 w-4 mr-2" />
              {loading ? 'Yükleniyor...' : 'Ara'}
            </Button>
          </div>
        </div>
      </div>

      {/* Results */}
      {aylikRapor && (
        <>
          {/* Summary */}
          <div className="bg-surface border border-border p-4 md:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 md:mb-6">
              <h2 className="text-base md:text-lg font-heading font-bold text-textPrimary">Genel Özet</h2>
              <Button
                onClick={handleExportCSV}
                data-testid="export-btn"
                variant="outline"
              >
                <FileDown className="h-4 w-4 mr-2" />
                CSV İndir
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              <div className="bg-surfaceSubtle p-3 md:p-4">
                <p className="text-xs md:text-sm text-textSecondary mb-1">Toplam Satış</p>
                <p className="text-lg md:text-2xl font-mono font-bold text-primary" data-testid="total-sales">
                  {formatCurrency(aylikRapor.toplamSatis)}
                </p>
              </div>
              <div className="bg-surfaceSubtle p-3 md:p-4">
                <p className="text-xs md:text-sm text-textSecondary mb-1">Nakit Satış</p>
                <p className="text-lg md:text-2xl font-mono font-bold text-textPrimary">
                  {formatCurrency(aylikRapor.nakitSatis)}
                </p>
              </div>
              <div className="bg-surfaceSubtle p-3 md:p-4">
                <p className="text-xs md:text-sm text-textSecondary mb-1">Gider Toplam</p>
                <p className="text-lg md:text-2xl font-mono font-bold text-error">
                  {formatCurrency(aylikRapor.nakitGiderToplam)}
                </p>
              </div>
              <div className="bg-surfaceSubtle p-3 md:p-4">
                <p className="text-xs md:text-sm text-textSecondary mb-1">NET</p>
                <p className="text-lg md:text-2xl font-mono font-bold text-success" data-testid="net-total">
                  {formatCurrency(aylikRapor.net)}
                </p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-textSecondary">Kart POS</p>
                <p className="font-mono font-bold text-textPrimary">{formatCurrency(aylikRapor.kartToplam)}</p>
              </div>
              <div>
                <p className="text-textSecondary">Havale</p>
                <p className="font-mono font-bold text-textPrimary">{formatCurrency(aylikRapor.havaleToplam)}</p>
              </div>
              <div>
                <p className="text-textSecondary">Veresiye</p>
                <p className="font-mono font-bold text-textPrimary">{formatCurrency(aylikRapor.veresiyeSatis)}</p>
              </div>
              <div>
                <p className="text-textSecondary">Kayıt Sayısı</p>
                <p className="font-mono font-bold text-textPrimary">{aylikRapor.kayitSayisi}</p>
              </div>
            </div>
          </div>

          {/* Payment Methods Chart */}
          <div className="bg-surface border border-border p-4 md:p-6">
            <h2 className="text-base md:text-lg font-heading font-bold text-textPrimary mb-3 md:mb-4">Ödeme Türü Dağılımı</h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={paymentMethodData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E7E5E4" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#57534E' }} />
                <YAxis tick={{ fontSize: 12, fill: '#57534E' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E7E5E4' }}
                  formatter={(value) => formatCurrency(value)}
                />
                <Bar dataKey="value" fill="#EA580C" name="Tutar" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Bank Report */}
          <div className="bg-surface border border-border p-4 md:p-6">
            <h2 className="text-base md:text-lg font-heading font-bold text-textPrimary mb-3 md:mb-4">Banka Bazlı Rapor</h2>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="banka-report-table">
                <thead className="bg-surfaceSubtle border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Banka</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-textSecondary uppercase tracking-wider">Kart POS</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-textSecondary uppercase tracking-wider">Havale</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-textSecondary uppercase tracking-wider">Toplam</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {bankaRapor.map((banka, index) => (
                    <tr key={index} data-testid={`banka-row-${index}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-textPrimary">{banka.ad}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-right text-textPrimary">{formatCurrency(banka.kartPos)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-right text-textPrimary">{formatCurrency(banka.havale)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-right font-bold text-primary">{formatCurrency(banka.toplam)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}